import { useEffect, useCallback } from "react"
import { X, MessageSquare, Clock, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { ConciergeMessages } from "./ConciergeMessages"
import { ConciergeInput } from "./ConciergeInput"
import type { ConciergeMessage } from "./types"

interface ConciergePanelProps {
  isOpen: boolean
  onClose: () => void
  messages: ConciergeMessage[]
  onSend: (text: string) => void
  onQuickAction: (action: string) => void
  isLoading: boolean
}

function getContextText(propertyName: string | undefined, page: string): string {
  if (!propertyName) return "Viewing your portfolio"

  const segment = page.split("/").filter(Boolean).pop() || ""

  switch (segment) {
    case "performance":
      return `Viewing ${propertyName} performance metrics`
    case "places":
      return `Exploring places near ${propertyName}`
    case "reviews":
      return `Reviewing guest feedback for ${propertyName}`
    case "listings":
      return `Analyzing listing intelligence for ${propertyName}`
    case "settings":
      return `Managing ${propertyName} settings`
    default:
      return `Viewing ${propertyName} overview`
  }
}

function getStarterChips(propertyName: string | undefined, page: string): string[] {
  if (!propertyName) {
    return ["How are my properties doing?", "Any occupancy concerns?", "Revenue trends"]
  }

  const segment = page.split("/").filter(Boolean).pop() || ""

  switch (segment) {
    case "performance":
      return [`Analyze ${propertyName}`, "Compare to competitors", "Rate optimization tips"]
    case "reviews":
      return ["Summarize recent reviews", "Any negative trends?", "Guest sentiment overview"]
    case "listings":
      return ["Listing quality check", "Compare to market", "Improvement suggestions"]
    default:
      return [`How is ${propertyName} doing?`, "Compare to competitors", "Any concerns?"]
  }
}

export function ConciergePanel({
  isOpen,
  onClose,
  messages,
  onSend,
  onQuickAction,
  isLoading,
}: ConciergePanelProps) {
  const { property } = usePropertyContext()

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose()
      }
    },
    [isOpen, onClose],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  const contextText = getContextText(property?.name, window.location.pathname)
  const starterChips = getStarterChips(property?.name, window.location.pathname)
  const isEmpty = messages.length === 0 && !isLoading

  return (
    <div
      className={cn(
        "fixed right-0 z-[200]",
        "top-16 h-[calc(100vh-64px)]",
        "w-full max-md:w-full md:w-[420px]",
        "bg-card/80 backdrop-blur-sm border-l border-border/50",
        "flex flex-col",
        "transition-transform duration-300 ease-out",
        isOpen ? "translate-x-0" : "translate-x-full",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-teal-50 flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">FRAME Concierge</div>
            <div className="text-[11px] text-muted-foreground">
              {property?.name ? `Viewing ${property.name}` : "Your portfolio"}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-muted-foreground hover:bg-gray-100 hover:text-foreground transition-colors bg-transparent border-0 cursor-pointer"
          aria-label="Close concierge"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* Context bar */}
      <div className="px-5 py-2.5 border-b border-border flex-shrink-0">
        <span className="inline-flex items-center gap-1.5 bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs">
          <Clock className="h-3 w-3 flex-shrink-0" />
          {contextText}
        </span>
      </div>

      {/* Welcome state or Messages */}
      {isEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 gap-4">
          <Sparkles className="h-8 w-8 text-muted-foreground" />
          <h3 className="font-display text-lg text-foreground">How can I help?</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {starterChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => onSend(chip)}
                className="px-3 py-1.5 text-xs border border-border rounded-full bg-card text-muted-foreground cursor-pointer transition-colors duration-150 hover:border-primary hover:text-primary hover:bg-teal-50 font-sans"
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <ConciergeMessages messages={messages} isLoading={isLoading} onQuickAction={onQuickAction} />
      )}

      {/* Input */}
      <ConciergeInput onSend={onSend} disabled={isLoading} autoFocus={isOpen} />

      {/* Footer */}
      <div className="py-2 px-5 text-center text-[10px] text-gray-400 flex-shrink-0">
        Powered by Stayframe Intelligence
      </div>
    </div>
  )
}
